// ─────────────────────────────────────────────────────────────────────────────
// RBAC — the single source of truth for authorization in Alba ERPC 2.0.
//
// Carried over (and tightened) from Alba ERP v1, which got this part right.
//
// THE ONE RULE: authorization decisions are made with `can(role, permission)`.
// Comparing roles directly (`role === "super_admin"`) is forbidden — it is how
// permission logic ends up duplicated in fifteen files and drifts apart.
// The lint config and the `Permission` union make the correct path the easy one.
//
//   Employment contract ──▶ ERP role ──▶ permission set ──▶ authorization
//
// An ERP role describes ACCESS LEVEL, never the job. A "Nursing Manager" is a
// job title stored on the contract; their ERP role may be `employee`.
// ─────────────────────────────────────────────────────────────────────────────

export const ERP_ROLES = [
  "super_admin",
  "hr_manager",
  "branch_manager",
  "department_manager",
  "employee",
] as const;

export type ErpRole = (typeof ERP_ROLES)[number];

export function isErpRole(value: unknown): value is ErpRole {
  return typeof value === "string" && (ERP_ROLES as readonly string[]).includes(value);
}

// ── Permissions ─────────────────────────────────────────────────────────────
// Each entry is an atom. Routers name the atom they need; they never reason
// about roles. Adding a capability to a role means editing ONE map below.

export const PERMISSIONS = {
  /** Read employee records (within the caller's data scope). */
  EMPLOYEES_READ: "employees:read",
  /** Create and edit employee records. */
  EMPLOYEES_WRITE: "employees:write",

  /** Read employment contracts. */
  CONTRACTS_READ: "contracts:read",
  /** Create, amend, renew and terminate contracts. */
  CONTRACTS_WRITE: "contracts:write",

  /** Read payroll records and payslips. */
  PAYROLL_READ: "payroll:read",
  /** Generate and edit draft payroll. */
  PAYROLL_WRITE: "payroll:write",
  /** Approve payroll and mark it paid. HR/executive only. */
  PAYROLL_APPROVE: "payroll:approve",

  /** Record and view attendance for others. */
  ATTENDANCE_READ: "attendance:read",
  /** Correct attendance records. */
  ATTENDANCE_WRITE: "attendance:write",
  /**
   * Correct YOUR OWN attendance record.
   *
   * Split off from `attendance:write` on 28 October 2026, because the two are
   * not the same act. A branch manager fixing a nurse's missed check-out is
   * supervision; the same manager fixing their own late arrival is marking
   * their own homework — and the register is what absence deductions are
   * computed from, so it is marking it in money.
   *
   * Held by `super_admin` alone, deliberately including HR: the owner asked
   * for the branch manager's own days to be the super administrator's to
   * correct, and the reason he gave applies to whoever else holds the pen.
   * It stops at the super administrator because the chain has to end
   * somewhere — there is nobody above him to fix his own day.
   *
   * This permission GRANTS nothing by itself. `attendance:write` is still
   * what opens corrections at all; this one only says the holder is not
   * excluded from their own row.
   */
  ATTENDANCE_CORRECT_SELF: "attendance:correct_self",

  /**
   * Decide leave requests — FOR YOUR OWN DIRECT REPORTS.
   *
   * The scope is the reporting line, not the branch and not the department:
   * `Actor.reportIds`, which is everybody whose `employees.manager_id` points
   * at you. `SCOPE_GLOBAL` widens it to everybody; nothing else does.
   *
   * Granted to every role including `employee`, because most managers in this
   * clinic hold no manager role — twelve people report to a Head Nurse whose
   * contract says `employee`. Holding the permission is not the same as being
   * able to use it: with no reports and no global scope it decides nothing.
   */
  LEAVE_APPROVE: "leave:approve",
  /** Administer leave policies, balances and encashments. */
  LEAVE_ADMIN: "leave:admin",

  /**
   * File a leave request on somebody else's behalf. Nothing else.
   *
   * Deliberately NOT part of `leave:admin`, which also carries `adjust` and
   * `encash` — handing out balance and converting it to cash. A branch manager
   * must be able to enter leave for staff who have no login; they must not be
   * able to grant balance or turn it into money.
   *
   * It is subject to every rule the employee would have been: the balance
   * check, the quota, the overlap. The route in changed, not the rules. And
   * the person who files cannot be the person who decides — see
   * `decideRequest` and `ck_leave_submitter_is_not_decider`.
   */
  LEAVE_SUBMIT_FOR: "leave:submit_for",
  /**
   * Open the certificate attached to somebody's leave — the doctor's note on a
   * sick day — for anyone inside the holder's data scope.
   *
   * Held by the super administrator and by a branch manager, for their own
   * branch. The employee's DIRECT MANAGER may open it too, but not through
   * this permission: a direct manager can hold any role, down to `employee` (a
   * head nurse is somebody's manager), so that route is the reporting line
   * itself — see `mayReadLeaveCertificate` in core/scope.ts, which is the one
   * place the three are combined.
   *
   * NOT held by HR, and not by a department manager on scope alone. A medical
   * certificate is health data; the owner named the three people who need it
   * (2026-09-21) and the list stops there.
   */
  LEAVE_CERTIFICATE_READ: "leave:certificate_read",
  /**
   * Delete a leave request outright — the row goes, and nothing of it shows to
   * the employee again.
   *
   * Deliberately NOT part of `leave:admin`. Cancelling is the ordinary way to
   * undo booked leave and it keeps the record: the request stays, its status
   * says cancelled, the days return to the balance and the audit trail explains
   * itself. Anybody who administers leave should be doing that. Destroying the
   * record is a different act with a different purpose — clearing test data,
   * or a request logged against the wrong person entirely — and it is not part
   * of running leave day to day.
   *
   * super_admin only, the same reasoning as `loans:override`: the deletion is
   * still written to the audit trail, so the request goes and the fact that
   * somebody removed it stays.
   */
  LEAVE_DELETE: "leave:delete",

  /** Approve or reject overtime. */
  OVERTIME_APPROVE: "overtime:approve",

  /**
   * Delete an employee record outright.
   *
   * For a person entered by mistake, and nothing else — the delete refuses
   * whenever an issued document, a payroll line, a loan or an activated
   * contract points at them, because those records outlive the record-keeping.
   * Ending an employment relationship is termination, which is a different act
   * with its own workflow and its own documents.
   *
   * super_admin only, the same reasoning as `leave:delete` and
   * `loans:override`: the act is still written to the audit trail, in a row
   * carrying the code and name as TEXT so it survives the person it names.
   */
  EMPLOYEES_DELETE: "employees:delete",

  /** Administer employee loans / salary advances. */
  LOANS_ADMIN: "loans:admin",
  /**
   * Bypass a loans business rule that exists to protect an ordinary user's
   * data from a mistake — never a rule about who may see or touch what, which
   * `loans:admin` and scope already decide. Holding this does not skip a
   * guard silently: the action still requires a typed reason and is still
   * written to the audit trail, in full, including what the value was before.
   * super_admin only, by design — see docs/DECISIONS.md.
   */
  LOANS_OVERRIDE: "loans:override",

  /** Generate official documents (certificates, letters, settlements). */
  DOCUMENTS_GENERATE: "documents:generate",
  /** Read the document archive. */
  DOCUMENTS_READ: "documents:read",

  /**
   * Destroy an issued document permanently, rather than voiding it.
   *
   * super_admin only, and separate from `DOCUMENTS_GENERATE` on purpose: the
   * people who issue documents are not the people who get to decide one never
   * existed. Voiding is the ordinary correction and stays with whoever can
   * generate; this is the owner of the system removing their own data.
   *
   * What survives is the audit row — `audit_log.entity_id` carries no foreign
   * key to `documents`, so the record of the deletion outlives the record it
   * describes. That is the whole reason this is safe to offer.
   */
  DOCUMENTS_DELETE: "documents:delete",

  /**
   * Open the Reports section.
   *
   * Deliberately NOT sufficient on its own. Each report also declares the
   * permission its DATA needs — the Payroll Register needs `payroll:read`, the
   * Contract Expiry report needs `contracts:read` — so Reports cannot become a
   * side door into figures somebody could not otherwise read. See
   * `src/domain/reports/catalogue.ts`.
   *
   * Withheld from `employee` and `department_manager`: reports are lists of
   * other people, and neither role reads other people's records anywhere else
   * in the system.
   */
  REPORTS_VIEW: "reports:view",

  /**
   * Open the organisation chart.
   *
   * Super administrator and HR only. Deliberately NOT held by a branch manager,
   * who reads their own branch everywhere else in the system — the chart is the
   * whole company's hierarchy with names, positions and contact details, and
   * that is an HR view rather than a management convenience.
   *
   * READ-ONLY by construction: there is no `orgchart:write`, because the chart
   * edits nothing. Reporting lines change in the Employees module, where the
   * change is validated against cycles and written to the audit trail.
   */
  ORGCHART_VIEW: "orgchart:view",

  /** Manage branches, departments and org configuration. */
  ORG_MANAGE: "org:manage",
  /** Change system-wide settings (leave types, holidays, roles). */
  SETTINGS_WRITE: "settings:write",

  /**
   * Apply the company stamp to an uploaded PDF.
   *
   * Its own permission on purpose. The tool reads no employee record and
   * produces no document the archive knows about, so it does not belong to
   * `DOCUMENTS_GENERATE` — but it applies the company seal, so it cannot be
   * open to every signed-in user either. Managing the stamp library is a
   * separate, narrower thing and stays on `SETTINGS_WRITE`.
   */
  STAMP_USE: "stamp:use",

  /**
   * Attach and remove personal files on an employee's record — a scanned
   * iqama, a certificate, a signed contract.
   *
   * Its own permission, held by the super administrator alone. This is not
   * `DOCUMENTS_GENERATE`: nothing here is generated, templated or referenced,
   * and the system never interprets what is uploaded. It is not
   * `EMPLOYEES_WRITE` either — that permission edits who a person IS, and
   * attaching a file about somebody changes nothing about them, which is the
   * same reasoning that already lets the directory generate a document.
   *
   * READING is gated on this too, and that is the whole point of it.
   *
   * This paragraph used to say the opposite — that anyone who may open the
   * record (`EMPLOYEES_READ`, within scope) could see and preview the papers
   * on it, because HR must check whether an iqama is about to expire. That
   * reasoning was overruled on 10 August 2026: a scanned iqama, a marriage
   * certificate, a medical report are the person's own papers, and a branch
   * manager being able to open them is not a convenience but a disclosure.
   *
   * So the permission covers the whole surface — list, read, upload, delete,
   * and the expiry feeds that merely COUNT them. Anything less leaks the fact
   * that a file exists, which is half of what was being withheld. Losing the
   * expiry warning is the accepted cost; it is a real cost, and it fell on
   * `hr_manager` as well as on the three roles that prompted the change.
   */
  EMPLOYEE_FILES_MANAGE: "employee_files:manage",

  /** See data across all branches. */
  SCOPE_GLOBAL: "scope:global",
  /** See data for the caller's own branch. */
  SCOPE_BRANCH: "scope:branch",
  /** See only the caller's own records. */
  SCOPE_SELF: "scope:self",

  /** May appear as an approver in the routing chain. */
  APPROVALS_ROUTE: "approvals:route",

  /**
   * Read one's own notifications.
   *
   * Held by EVERYONE. This is the narrow half of the mailbox: an employee sees
   * what the system has told them, and nothing else. It does not open the Mail
   * Center, cannot compose, and cannot reach another folder.
   */
  MAIL_NOTIFICATIONS: "mail:notifications",
  /**
   * The full Mail Center: compose, folders, labels, filters, signatures.
   *
   * Deliberately NOT held by `employee`. Correspondence with the outside world
   * is a management function, and an employee's mailbox is their notification
   * list rather than an email client.
   */
  MAIL_USE: "mail:use",
  /** Read any mailbox and the full delivery log. An oversight power. */
  MAIL_AUDIT: "mail:audit",
  /** Create and edit shared message templates. */
  MAIL_TEMPLATES: "mail:templates",
  /** Configure SMTP accounts, including credentials. */
  MAIL_ACCOUNTS: "mail:accounts",

  /**
   * Turn performance management on or off, and decide whether employees may
   * see their own completed evaluations. The switch only — this does NOT grant
   * sight of anybody's evaluation.
   */
  KPI_ADMINISTER: "kpi:administer",
  /**
   * Run evaluation cycles for one's own direct reports.
   *
   * Held by every role that can have direct reports. Holding it is necessary
   * but NOT sufficient: the module must be switched on, and a manager sees only
   * the people who actually report to them. Both are enforced server-side.
   */
  KPI_EVALUATE: "kpi:evaluate",

  /**
   * Keep a task list: personal tasks, and the tasks assigned to you.
   * Held by everyone — a task list is not a privilege.
   */
  TASKS_USE: "tasks:use",
  /**
   * Assign tasks to your own direct reports, and see how they are getting on.
   * Necessary but not sufficient: you still only see the people who report
   * directly to you.
   */
  TASKS_ASSIGN: "tasks:assign",
  /**
   * See and edit somebody else's PERSONAL tasks.
   *
   * The explicit permission the clinic asked for. A manager does NOT have it:
   * a personal task is private by default, and overriding that is a deliberate
   * grant, not a side effect of being someone's manager.
   */
  TASKS_ADMIN: "tasks:admin",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ── Profiles ────────────────────────────────────────────────────────────────
// Exhaustive by design: a permission's ABSENCE is as deliberate as its presence.
// `satisfies` makes the compiler reject a role that is added without a profile.

const PROFILES = {
  super_admin: [
    PERMISSIONS.EMPLOYEES_READ, PERMISSIONS.EMPLOYEES_WRITE, PERMISSIONS.EMPLOYEES_DELETE,
    PERMISSIONS.CONTRACTS_READ, PERMISSIONS.CONTRACTS_WRITE,
    PERMISSIONS.PAYROLL_READ, PERMISSIONS.PAYROLL_WRITE, PERMISSIONS.PAYROLL_APPROVE,
    PERMISSIONS.ATTENDANCE_READ, PERMISSIONS.ATTENDANCE_WRITE,
    PERMISSIONS.ATTENDANCE_CORRECT_SELF,
    PERMISSIONS.LEAVE_APPROVE, PERMISSIONS.LEAVE_ADMIN, PERMISSIONS.LEAVE_DELETE,
    PERMISSIONS.LEAVE_SUBMIT_FOR, PERMISSIONS.LEAVE_CERTIFICATE_READ,
    PERMISSIONS.OVERTIME_APPROVE,
    PERMISSIONS.LOANS_ADMIN, PERMISSIONS.LOANS_OVERRIDE,
    PERMISSIONS.DOCUMENTS_GENERATE, PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_DELETE,
    PERMISSIONS.REPORTS_VIEW, PERMISSIONS.ORGCHART_VIEW,
    PERMISSIONS.ORG_MANAGE, PERMISSIONS.SETTINGS_WRITE,
    PERMISSIONS.STAMP_USE,
    PERMISSIONS.EMPLOYEE_FILES_MANAGE,
    PERMISSIONS.SCOPE_GLOBAL,
    PERMISSIONS.APPROVALS_ROUTE,
    PERMISSIONS.MAIL_NOTIFICATIONS,
    PERMISSIONS.MAIL_USE, PERMISSIONS.MAIL_AUDIT,
    PERMISSIONS.MAIL_TEMPLATES, PERMISSIONS.MAIL_ACCOUNTS,
    // The switch only. Deliberately NOT KPI_EVALUATE: the rule is that only
    // direct managers see evaluations, and the super administrator is not
    // exempt from it. See docs/KPI.md.
    PERMISSIONS.KPI_ADMINISTER,
    PERMISSIONS.TASKS_USE, PERMISSIONS.TASKS_ASSIGN, PERMISSIONS.TASKS_ADMIN,
  ],

  hr_manager: [
    PERMISSIONS.EMPLOYEES_READ, PERMISSIONS.EMPLOYEES_WRITE,
    PERMISSIONS.CONTRACTS_READ, PERMISSIONS.CONTRACTS_WRITE,
    PERMISSIONS.PAYROLL_READ, PERMISSIONS.PAYROLL_WRITE, PERMISSIONS.PAYROLL_APPROVE,
    PERMISSIONS.ATTENDANCE_READ, PERMISSIONS.ATTENDANCE_WRITE,
    PERMISSIONS.LEAVE_APPROVE, PERMISSIONS.LEAVE_ADMIN, PERMISSIONS.LEAVE_SUBMIT_FOR,
    PERMISSIONS.OVERTIME_APPROVE,
    PERMISSIONS.LOANS_ADMIN,
    PERMISSIONS.DOCUMENTS_GENERATE, PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.REPORTS_VIEW, PERMISSIONS.ORGCHART_VIEW,
    PERMISSIONS.STAMP_USE,
    PERMISSIONS.SCOPE_GLOBAL,
    PERMISSIONS.APPROVALS_ROUTE,
    // HR corresponds with staff and owns the template library, but SMTP
    // credentials stay with the super administrator.
    PERMISSIONS.MAIL_NOTIFICATIONS,
    PERMISSIONS.MAIL_USE, PERMISSIONS.MAIL_AUDIT, PERMISSIONS.MAIL_TEMPLATES,
    PERMISSIONS.KPI_EVALUATE,
    PERMISSIONS.TASKS_USE, PERMISSIONS.TASKS_ASSIGN,
  ],

  /**
   * Runs a branch. Sees the PEOPLE in it and nothing else about the business.
   *
   * It used to reach payroll, contracts, employee documents, the documents
   * generator, reports, and the Mail Center. All of it is gone, and none of it
   * went by hiding a menu item — the permission is what was removed, so the
   * procedure refuses and the section is not offered because it cannot be used.
   *
   * Reports went whole rather than filtered. They carry figures a branch
   * manager should not see, and a report that has to be partially hidden is the
   * wrong report for the role, not a report with the wrong filter.
   *
   * `LEAVE_APPROVE` is still here but no longer means the branch — see the note
   * on the permission itself. Ahmad decides for the five people who report to
   * him because he is their manager, not because he runs their building.
   */
  branch_manager: [
    PERMISSIONS.EMPLOYEES_READ,
    PERMISSIONS.ATTENDANCE_READ, PERMISSIONS.ATTENDANCE_WRITE,
    PERMISSIONS.LEAVE_APPROVE,
    // Enter leave for their own staff. Most people here have no login, so
    // without this their leave cannot be recorded by anybody who is present.
    // Narrower than `leave:admin` on purpose: no adjust, no encash. It is also
    // the one thing that stays BRANCH-wide while approval narrows to reports —
    // filing on somebody's behalf is clerical, deciding is authority.
    PERMISSIONS.LEAVE_SUBMIT_FOR,
    // The certificate on that leave, for their branch. They file sick leave
    // with a doctor's note attached, and need to be able to check the note
    // they attached is the right one.
    PERMISSIONS.LEAVE_CERTIFICATE_READ,
    PERMISSIONS.OVERTIME_APPROVE,
    PERMISSIONS.SCOPE_BRANCH,
    PERMISSIONS.APPROVALS_ROUTE,
    // Notifications, not the Mail Center. Being told a request needs a decision
    // is not the same capability as corresponding with the whole clinic.
    PERMISSIONS.MAIL_NOTIFICATIONS,
    PERMISSIONS.KPI_EVALUATE,
    PERMISSIONS.TASKS_USE, PERMISSIONS.TASKS_ASSIGN,
  ],

  /**
   * Runs a department. The same shape as a branch manager, over a smaller set
   * of people: their own direct reports rather than a whole branch.
   *
   * It used to hold `LEAVE_APPROVE` together with `SCOPE_SELF`, which is a
   * permission that can never fire — the scope limited every query to the
   * holder, so the approval page was empty and the authority was theoretical.
   * That pairing is why a department manager appeared to do nothing at all.
   */
  department_manager: [
    PERMISSIONS.EMPLOYEES_READ,
    PERMISSIONS.ATTENDANCE_READ,
    PERMISSIONS.LEAVE_APPROVE,
    PERMISSIONS.OVERTIME_APPROVE,
    PERMISSIONS.SCOPE_SELF,
    PERMISSIONS.APPROVALS_ROUTE,
    PERMISSIONS.MAIL_NOTIFICATIONS,
    PERMISSIONS.KPI_EVALUATE,
    PERMISSIONS.TASKS_USE, PERMISSIONS.TASKS_ASSIGN,
  ],

  employee: [
    PERMISSIONS.SCOPE_SELF,
    // Notifications, NOT the Mail Center. An employee must see what the system
    // tells them — a leave decision, a payslip notice — but composing and
    // corresponding is a management function.
    PERMISSIONS.MAIL_NOTIFICATIONS,
    // A senior nurse with an ERP role of `employee` can still be somebody's
    // direct manager. The permission opens the door; having actual reports is
    // what puts anything behind it.
    PERMISSIONS.KPI_EVALUATE,
    /**
     * AND SO CAN APPROVE THEIR LEAVE. Same reasoning, and it is not optional.
     *
     * Twelve of this clinic's staff report to a Head Nurse whose ERP role is
     * `employee`. Restricting approval to manager ROLES left those twelve with
     * nobody able to decide their leave — the authority follows the reporting
     * line, and the reporting line does not care what the contract calls you.
     */
    PERMISSIONS.LEAVE_APPROVE,
    // Everyone keeps a task list. Assigning is the same story as evaluating:
    // the permission opens the door, having reports is what is behind it.
    PERMISSIONS.TASKS_USE, PERMISSIONS.TASKS_ASSIGN,
  ],
} as const satisfies Record<ErpRole, readonly Permission[]>;

/** The permission set for a role. Unknown input degrades to `employee`. */
export function permissionsFor(role: string | null | undefined): readonly Permission[] {
  return isErpRole(role) ? PROFILES[role] : PROFILES.employee;
}

/**
 * THE authorization primitive. Every access decision in the server and the UI
 * goes through this function.
 */
export function can(role: string | null | undefined, permission: Permission): boolean {
  return (permissionsFor(role) as readonly string[]).includes(permission);
}

/** Convenience predicates — thin wrappers, never separate logic. */
export const hasGlobalScope = (role: string | null | undefined): boolean => can(role, PERMISSIONS.SCOPE_GLOBAL);
export const hasBranchScope = (role: string | null | undefined): boolean => can(role, PERMISSIONS.SCOPE_BRANCH);

// ── Data scope ──────────────────────────────────────────────────────────────

/**
 * How far a caller can see. Resolved once per request in the tRPC context and
 * then passed explicitly to every repository query, so no query can
 * accidentally forget to filter.
 */
export type DataScope =
  | { readonly kind: "global" }
  | { readonly kind: "branch"; readonly branchIds: readonly number[] }
  | { readonly kind: "self"; readonly employeeId: number };

export function resolveScope(
  role: string | null | undefined,
  actor: { employeeId: number; branchId: number | null; managedBranchIds: readonly number[] },
): DataScope {
  if (hasGlobalScope(role)) return { kind: "global" };
  if (hasBranchScope(role)) {
    const ids = new Set<number>(actor.managedBranchIds);
    if (actor.branchId !== null) ids.add(actor.branchId);
    return { kind: "branch", branchIds: [...ids] };
  }
  return { kind: "self", employeeId: actor.employeeId };
}

/** Whether a scope permits reading a specific employee's record. */
export function scopeAllowsEmployee(
  scope: DataScope,
  target: { employeeId: number; branchId: number | null },
): boolean {
  switch (scope.kind) {
    case "global": return true;
    case "branch": return target.branchId !== null && scope.branchIds.includes(target.branchId);
    case "self": return scope.employeeId === target.employeeId;
  }
}
